import React, { useState, useEffect } from 'react';
import { edefterFetch, edefterAuthHeaders } from '../../services/edefterApi';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  AttachMoney,
  CalendarToday,
  Business,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const EDEFTER_API_URL = window.APP_CONFIG?.edefterApiUrl || 'https://srv.dpg.net.tr/edefter';

const EdefterUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const fetchMizanList = async () => {
    try {
      const response = await edefterFetch(`${EDEFTER_API_URL}/mizan-list`);
      if (response.ok) {
        const data = await response.json();
        setMizanData(data);
      }
    } catch (err) {
      console.error('Failed to fetch trial balance list:', err);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/xml' && !file.name.endsWith('.xml')) {
        toast.error('Please select an XML file.');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
        toast.warning('Please select an XML file.');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    let processingInterval = null;
    let currentProgressValue = 70; // Local variable to track progress

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const xhr = new XMLHttpRequest();
      
      // Progress tracking - upload 0-70% arası
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          // Upload progress'i 0-70% arasında göster
          const uploadPercent = Math.round((e.loaded / e.total) * 70);
          currentProgressValue = uploadPercent;
          setUploadProgress(uploadPercent);
        }
      });

      // Backend işlemesi için tahmini progress (70-100%)
      const startProcessingProgress = () => {
        currentProgressValue = 70;
        
        processingInterval = setInterval(() => {
          // Her 150ms'de %1-2 arası rastgele artış (daha doğal görünmesi için)
          const increment = 1 + Math.random(); // 1-2 arası
          currentProgressValue += increment;
          
          if (currentProgressValue >= 95) {
            setUploadProgress(95);
            clearInterval(processingInterval);
            processingInterval = null;
          } else {
            setUploadProgress(Math.round(currentProgressValue));
          }
        }, 150);
      };

      const response = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Upload tamamlandı (%70), şimdi backend işlemesi başlıyor
            currentProgressValue = 70;
            setUploadProgress(70);
            startProcessingProgress();
            resolve(xhr);
          } else {
            if (processingInterval) clearInterval(processingInterval);
            reject(new Error(xhr.statusText || 'Upload failed'));
          }
        };
        xhr.onerror = () => {
          if (processingInterval) clearInterval(processingInterval);
          reject(new Error('Network error'));
        };
        xhr.responseType = 'blob';
        xhr.open('POST', `${EDEFTER_API_URL}/upload-xml`);
        const _auth = edefterAuthHeaders();
        if (_auth.Authorization) xhr.setRequestHeader('Authorization', _auth.Authorization);
        xhr.send(formData);
      });

      // Backend işlemesi için progress'in artmasını bekle
      // Interval çalışırken progress %95'e kadar çıkacak
      while (processingInterval && currentProgressValue < 95) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Backend işlemesi tamamlandı, interval'i temizle ve %100 yap
      if (processingInterval) {
        clearInterval(processingInterval);
        processingInterval = null;
      }
      setUploadProgress(100);

      // Excel dosyasını indir
      const blob = response.response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mizan.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File processed successfully and Excel file downloaded.');

      // Upload response header'ından document ID'yi al
      const documentId = response.getResponseHeader('X-Document-Id');
      
      // Eğer document ID varsa, o dökümanın mizanını çek, yoksa son dökümanın mizanını çek
      if (documentId) {
        try {
          const mizanResponse = await edefterFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${documentId}`);
          if (mizanResponse.ok) {
            const mizanData = await mizanResponse.json();
            setMizanData(mizanData);
          } else {
            // Fallback: son dökümanın mizanını çek
            await fetchMizanList();
          }
        } catch (err) {
          console.error('Error fetching mizan by document:', err);
          // Fallback: son dökümanın mizanını çek
          await fetchMizanList();
        }
      } else {
        // Document ID yoksa, son dökümanın mizanını çek
        await fetchMizanList();
      }
      setSelectedFile(null);
      setUploadProgress(0);
      const fileInput = document.getElementById('fileInput');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'An error occurred while uploading the file.');
      toast.error(err.message || 'An error occurred while uploading the file.');
      setUploadProgress(0);
      // Interval'i temizle
      if (processingInterval) clearInterval(processingInterval);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            {mizanData && mizanData.companyInfo ? (
              <Box>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <Avatar sx={{ 
                      width: 64, 
                      height: 64,
                      background: 'rgba(255,255,255,0.25)',
                      border: '3px solid rgba(255,255,255,0.3)'
                    }}>
                      <Business sx={{ fontSize: '1.5rem' }} />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {mizanData.companyInfo.title || 'Firma Bilgileri'}
                    </Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                <Grid container spacing={3}>
                  {mizanData.companyInfo.taxNumber && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Vergi Kimlik No
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {mizanData.companyInfo.taxNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {mizanData.companyInfo.taxOffice && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccountBalance sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Vergi Dairesi
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {mizanData.companyInfo.taxOffice}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {mizanData.companyInfo.email && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Email sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            E-posta
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {mizanData.companyInfo.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {mizanData.companyInfo.phone && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Phone sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Telefon
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {mizanData.companyInfo.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {mizanData.companyInfo.address && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocationOn sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Adres
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {mizanData.companyInfo.address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar sx={{ 
                    width: 64, 
                    height: 64,
                    background: 'rgba(255,255,255,0.25)',
                    border: '3px solid rgba(255,255,255,0.3)'
                  }}>
                    <Description sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Upload General Journal
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Upload General Journal XML files to generate trial balance reports
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* File Upload Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudUpload sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                  General Journal File (.xml)
                </Typography>
              </Box>

              <Paper
                sx={{
                  p: 4,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    borderColor: 'primary.dark',
                  },
                }}
                onClick={() => document.getElementById('fileInput').click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept=".xml"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="primary" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Upload XML File'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile ? 'File selected. Click the button to upload.' : 'Upload General Journal file in XML format'}
                </Typography>
              </Paper>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading || !selectedFile}
                    sx={{
                      minWidth: 200,
                      py: 1.5,
                      color: 'white',
                      borderRadius: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        color: 'white',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      }
                    }}
                  >
                    {loading ? `Uploading...` : 'Upload and Generate Trial Balance'}
                  </Button>
                </Box>
                {loading && (
                  <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
                    <LinearProgress 
                      variant="determinate"
                      value={uploadProgress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      {uploadProgress < 70 
                        ? `Uploading file... ${uploadProgress}%` 
                        : uploadProgress < 100
                        ? `Processing... ${uploadProgress}%`
                        : 'Completed!'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Trial Balance Table */}
            {mizanData && mizanData.rows && mizanData.rows.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Typography variant="h4" fontWeight="600">
                    Trial Balance
                  </Typography>
                  {mizanData.periodCoveredStart && mizanData.periodCoveredEnd && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                      <CalendarToday sx={{ fontSize: 24, color: 'text.secondary' }} />
                      <Typography variant="h5" color="text.secondary">
                        {formatDate(mizanData.periodCoveredStart)} - {formatDate(mizanData.periodCoveredEnd)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Card elevation={8} sx={{ borderRadius: 3 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                            Hesap Kodu
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                            Alt Kod
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                            Hesap Adı
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                            Hesap Detay Adı
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>
                            Borç
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>
                            Alacak
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>
                            Borç Bakiyesi
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>
                            Alacak Bakiyesi
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mizanData.rows.map((row, index) => (
                          <TableRow 
                            key={index}
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                              },
                              borderLeft: '4px solid transparent'
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                              {row.hesapKodu || ''}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {row.subId || ''}
                            </TableCell>
                            <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                              {row.mainDescription || ''}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {row.subDescription || ''}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.borc)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: '#1976d2', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacak)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'success.main', fontSize: '0.8rem' }}>
                              {formatNumber(row.borcBakiyesi)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'error.main', fontSize: '0.8rem' }}>
                              {formatNumber(row.alacakBakiyesi)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            )}

            {/* Empty State */}
            {!mizanData && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}>
                  <Description sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>
                  No File Uploaded Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Upload a General Journal XML file to view the trial balance report.
                </Typography>
              </Box>
            )}

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EdefterUpload;
